import { useCallback, useEffect, useState } from 'react';
import {
  getCachedProgressDashboard,
  loadServerPrimaryDashboard,
} from '../services/progressSyncService';

export default function useProgressDashboard(
  userId,
  limit = 100,
  refreshKey = ''
) {
  const [dashboard, setDashboard] = useState(() =>
    getCachedProgressDashboard(userId)
  );
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!userId) {
      const local = getCachedProgressDashboard(null);
      setDashboard(local);
      setLoading(false);
      return local;
    }

    setLoading(true);
    setError(null);
    try {
      const next = await loadServerPrimaryDashboard(userId, limit);
      setDashboard(next);
      return next;
    } catch (reloadError) {
      setError(reloadError);
      const fallback = getCachedProgressDashboard(userId);
      setDashboard(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  }, [userId, limit]);

  useEffect(() => {
    let active = true;

    if (!userId) {
      setDashboard(getCachedProgressDashboard(null));
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setDashboard(getCachedProgressDashboard(userId));
    setLoading(true);
    setError(null);

    loadServerPrimaryDashboard(userId, limit)
      .then((next) => {
        if (active) setDashboard(next);
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError);
          setDashboard(getCachedProgressDashboard(userId));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [userId, limit, refreshKey]);

  return {
    dashboard,
    loading,
    reload,
    error,
    source: dashboard?.source || 'local',
  };
}
